// ratingFlowLogger.js - Comprehensive logging utility with event emitter

import safeStorage from '../../utility/safeStorage';

class RatingFlowLogger {
  constructor() {
    this.logs = [];
    this.currentSession = null;
    this.listeners = [];
    this.loadFromStorage();
  }

  // Event emitter for real-time updates
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.getCurrentState());
      } catch (e) {
        console.error('Error notifying listener:', e);
      }
    });
  }

  getCurrentState() {
    return {
      currentSession: this.currentSession,
      archivedLogs: this.logs,
      timestamp: new Date().toISOString()
    };
  }

  // Start a new logging session for a book rating
  startSession(bookId, bookName) {
    this.currentSession = {
      sessionId: `${bookId}_${Date.now()}`,
      bookId,
      bookName,
      startTime: new Date().toISOString(),
      steps: [],
      errors: [],
      finalStatus: 'IN_PROGRESS'
    };
    
    this.log('SESSION_START', {
      bookId,
      bookName,
      userId: safeStorage.getItem('userId'),
      isLoggedIn: !!safeStorage.getItem('accessToken')
    });
    
    return this.currentSession.sessionId;
  }

  // Log a step in the rating flow
  log(stepName, data = {}) {
    if (!this.currentSession) {
      console.warn('No active session. Creating emergency session.');
      this.currentSession = {
        sessionId: `emergency_${Date.now()}`,
        startTime: new Date().toISOString(),
        steps: [],
        errors: [],
        finalStatus: 'EMERGENCY_SESSION'
      };
    }

    const logEntry = {
      timestamp: new Date().toISOString(),
      step: stepName,
      data: { ...data },
      stackTrace: new Error().stack
    };

    this.currentSession.steps.push(logEntry);
    // Store and notify
    this.saveToStorage();
    this.notifyListeners();
  }

  // Log an error
  logError(stepName, error, context = {}) {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      step: stepName,
      error: {
        message: error.message || error,
        stack: error.stack,
        name: error.name
      },
      context
    };

    if (this.currentSession) {
      this.currentSession.errors.push(errorEntry);
      this.currentSession.finalStatus = 'ERROR';
    }

    console.error(`[RATING_FLOW_ERROR] ${stepName}:`, error, context);
    this.saveToStorage();
    this.notifyListeners();
  }

  // End the current session
  endSession(status = 'COMPLETED') {
    if (!this.currentSession) {
      console.warn('No active session to end');
      return;
    }

    this.currentSession.endTime = new Date().toISOString();
    this.currentSession.finalStatus = status;
    this.currentSession.duration = 
      new Date(this.currentSession.endTime) - new Date(this.currentSession.startTime);

    this.log('SESSION_END', {
      status,
      totalSteps: this.currentSession.steps.length,
      hadErrors: this.currentSession.errors.length > 0
    });

    // Archive the session
    this.logs.push({ ...this.currentSession });
    this.saveToStorage();
    
    const sessionData = { ...this.currentSession };
    this.currentSession = null;
    
    this.notifyListeners();
    
    return sessionData;
  }

  // Save logs to localStorage
  saveToStorage() {
    try {
      const logsToSave = {
        currentSession: this.currentSession,
        archivedLogs: this.logs.slice(-10) // Keep last 10 sessions
      };
      safeStorage.setItem('ratingFlowLogs', JSON.stringify(logsToSave));
    } catch (e) {
      console.error('Failed to save logs to storage:', e);
    }
  }

  // Load logs from localStorage
  loadFromStorage() {
    try {
      const saved = safeStorage.getItem('ratingFlowLogs');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.currentSession = parsed.currentSession;
        this.logs = parsed.archivedLogs || [];
      }
    } catch (e) {
      console.error('Failed to load logs from storage:', e);
    }
  }

  // Get all logs for a specific book
  getLogsForBook(bookId) {
    return this.logs.filter(log => log.bookId === bookId);
  }

  // Get current session logs
  getCurrentSession() {
    return this.currentSession;
  }

  // Get all archived logs
  getAllLogs() {
    return {
      currentSession: this.currentSession,
      archivedLogs: this.logs
    };
  }

  // Export logs as downloadable JSON
  exportLogs() {
    const exportData = {
      exportDate: new Date().toISOString(),
      currentSession: this.currentSession,
      archivedLogs: this.logs
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], 
      { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rating-flow-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Clear all logs
  clearLogs() {
    this.logs = [];
    this.currentSession = null;
    safeStorage.removeItem('ratingFlowLogs');
    this.notifyListeners();
  }

  // Generate a summary report
  generateReport() {
    const report = {
      totalSessions: this.logs.length,
      successfulSessions: this.logs.filter(l => l.finalStatus === 'COMPLETED').length,
      failedSessions: this.logs.filter(l => l.finalStatus === 'ERROR').length,
      inProgressSessions: this.logs.filter(l => l.finalStatus === 'IN_PROGRESS').length,
      cancelledSessions: this.logs.filter(l => l.finalStatus === 'CANCELLED').length,
      commonErrors: {},
      averageDuration: 0,
      currentSession: this.currentSession
    };

    // Calculate common errors
    this.logs.forEach(log => {
      log.errors.forEach(err => {
        const key = err.error.message || 'Unknown Error';
        report.commonErrors[key] = (report.commonErrors[key] || 0) + 1;
      });
    });

    // Calculate average duration
    const completedSessions = this.logs.filter(l => l.duration);
    if (completedSessions.length > 0) {
      report.averageDuration = 
        completedSessions.reduce((sum, l) => sum + l.duration, 0) / 
        completedSessions.length;
    }

    return report;
  }
}

// Create singleton instance
const ratingLogger = new RatingFlowLogger();

// Make it globally accessible
if (typeof window !== 'undefined') {
  window.ratingLogger = ratingLogger;
}

export default ratingLogger;